import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Wallet } from "lucide-react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/constants";
import QuoteCreateModal from "@/components/accounting/QuoteCreateModal";
import { Button } from "@/components/ui/button";

const INV_STATUS = { brouillon: "Brouillon", validee: "Validée", envoyee: "Envoyée", partiellement_payee: "Partielle", payee: "Payée" };
const QUOTE_STATUS = { brouillon: "Brouillon", envoye: "Envoyé", accepte: "Accepté", refuse: "Refusé", converti: "Converti" };

export default function ClientFinanceTab({ clientId }) {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get(`/accounting/clients/${clientId}/finance`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const openQuoteModal = () => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    setQuoteOpen(true);
  };

  if (!data) return null;
  const { totals, invoices, quotes } = data;

  return (
    <div className="space-y-5" data-testid="client-finance-tab">
      <div className="grid grid-cols-3 gap-3">
        {[
          ["Facturé", totals.facture, "kpi-facture"],
          ["Encaissé", totals.encaisse, "kpi-encaisse"],
          ["Reste dû", totals.reste, "kpi-reste"],
        ].map(([l, v, t]) => (
          <div key={t} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-4" data-testid={`finance-${t}`}>
            <p className="text-[11px] uppercase tracking-wider text-neutral-400">{l}</p>
            <p className="font-heading font-bold text-xl mt-1">{fmtEuro(v)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        <p className="px-4 py-3 text-sm font-semibold flex items-center gap-2">
          <Wallet className="w-4 h-4 text-neutral-400" /> Factures
        </p>
        {invoices.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-neutral-400" data-testid="finance-no-invoice">Aucune facture pour ce client.</p>
        ) : invoices.map((inv) => (
          <button key={inv.id} onClick={() => navigate("/comptabilite?tab=factures")}
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm border-t border-neutral-100 hover:bg-neutral-50 transition-colors text-left"
            data-testid={`finance-invoice-${inv.id}`}>
            <span className="truncate"><strong>{inv.number || "Brouillon"}</strong>{inv.title ? ` · ${inv.title}` : ""}</span>
            <span className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-neutral-500">{INV_STATUS[inv.status] || inv.status}</span>
              <span className="font-medium">{fmtEuro(inv.total_ttc)}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-neutral-400" /> Devis
          </p>
          <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={openQuoteModal} data-testid="finance-new-quote-btn">
            <Plus className="w-3.5 h-3.5" /> Générer un devis
          </Button>
        </div>
        {quotes.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-neutral-400" data-testid="finance-no-quote">Aucun devis pour ce client.</p>
        ) : quotes.map((q) => (
          <button key={q.id} onClick={() => navigate("/comptabilite?tab=devis")}
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm border-t border-neutral-100 hover:bg-neutral-50 transition-colors text-left"
            data-testid={`finance-quote-${q.id}`}>
            <span className="truncate"><strong>{q.number || "Brouillon"}</strong></span>
            <span className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-neutral-500">{QUOTE_STATUS[q.status] || q.status}</span>
              <span className="font-medium">{fmtEuro(q.total_ttc)}</span>
            </span>
          </button>
        ))}
      </div>

      <QuoteCreateModal open={quoteOpen} onClose={() => setQuoteOpen(false)}
        onSaved={() => { setQuoteOpen(false); load(); }}
        clients={clients} presetClientId={clientId} />
    </div>
  );
}
